import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { delay, map, of, Subject } from 'rxjs';
import { LIB_CONFIG } from '../../lib.config';
import { Bildungsangebot } from '../../model';

import { ANBIETER_LOGO_FALLBACK } from '../../shared/utils/anbieter-logo-fallback';
import { CleanupService } from '../../shared/utils/cleanup.service';
import {
  FavoriteToggleComponent,
  Weiterbildungsangebot
} from '../detail/favorite-toggle/favorite-toggle.component';
import { ResponsiveCollapseComponent } from '../responsive-collapse/responsive-collapse.component';
import { TerminTabelleKachelComponent } from './termin-tabelle-kachel/termin-tabelle-kachel.component';
import { getLabelFromWeiterbildungsart } from '../../model/weiterbildungsarten';
import { ScreenSizeService } from '../../services/screen-size';
import { ScrollToAndFocusDirective } from '../../shared/directives';

@Component({
  selector: 'now-wbsuche-lib-angebot-termine-kachel',
  templateUrl: './angebot-termine-kachel.component.html',
  styleUrl: './angebot-termine-kachel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TerminTabelleKachelComponent,
    ResponsiveCollapseComponent,
    FavoriteToggleComponent,
    ScrollToAndFocusDirective
  ],
  providers: [{ provide: CleanupService }],
  encapsulation: ViewEncapsulation.None
})
export class AngebotTermineKachelComponent implements OnChanges {
  private readonly screenSizeService = inject(ScreenSizeService);

  private _angebot!: Bildungsangebot;

  @ViewChild('angebotKachel')
  angebotElementRef!: ElementRef<HTMLElement>;

  public isNowWbSuche = inject(LIB_CONFIG).isNowWbSuche;

  // Damit Screenreader den Inhalt einer aria-live Region vorlesen,
  // muss erst das aria-live Element gerendert und vom Screenreader registriert werden
  // und anschließend sich der Inhalt ändern.
  // Deshalb hier ein kurzes delay, bevor die Weiterbildungsinhalte gerendert werden.
  public showAfterInitialDelay$ = of(null).pipe(
    delay(1),
    map(() => true)
  );

  @Input()
  set angebot(value: Bildungsangebot) {
    this.updateCollapse$.next(true);
    if (this._angebot?.id != value?.id) {
      of(null).subscribe(() => {
        this.goToHeading();
      });
    }
    this._angebot = value;
  }

  @Output()
  terminSelected = new EventEmitter<string>();

  @ViewChild('heading')
  private readonly headingElement!: ElementRef<HTMLElement>;

  get angebot(): Bildungsangebot {
    return this._angebot;
  }

  public updateCollapse$ = new Subject<boolean>();

  public isDesktop$ = this.screenSizeService.onlyLargeDesktop$;

  public hasImgError = false;

  public selectTermin(termindId: string) {
    this.terminSelected.emit(termindId);
  }

  public goToHeading() {
    this.headingElement?.nativeElement.scrollIntoView();
    this.headingElement?.nativeElement.focus();
  }

  get anbieterLogo(): string {
    return this.hasImgError
      ? ANBIETER_LOGO_FALLBACK
      : (this.angebot.bildungsanbieter?.logo ?? ANBIETER_LOGO_FALLBACK);
  }

  get favoriteEntity(): Weiterbildungsangebot {
    return {
      basisdienst: 'WEITERBILDUNGSANGEBOT',
      favoritTitle: this.angebot.titel,
      targetId: String(this.angebot.id),
      detailsAnbieter: this.angebot.bildungsanbieter.name
    };
  }

  get weiterbildungsart(): string {
    return getLabelFromWeiterbildungsart(this.angebot.weiterbildungsart);
  }

  ngOnChanges(): void {
    //Wenn ein Angebot angeklickt wird, soll zum Anfang des Angebots gescrollt werden.
    this.angebotElementRef?.nativeElement?.scrollIntoView();
  }
}
